import React, { useState } from 'react';
import { X, Key, User, AlertCircle, CheckCircle, ExternalLink, Shield } from 'lucide-react';
import { setOSMCredentials, testOSMConnection, getOSMUserDetails, clearOSMCredentials } from '../services/osmApi';

interface OSMAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthenticated: () => void;
}

export const OSMAuthModal: React.FC<OSMAuthModalProps> = ({
  isOpen,
  onClose,
  onAuthenticated
}) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);

  if (!isOpen) return null;

  const handleAuthenticate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError(null);

    try {
      // Set credentials
      setOSMCredentials(credentials.username, credentials.password);
      
      // Test connection
      const isConnected = await testOSMConnection();
      
      if (!isConnected) {
        throw new Error('Authentication failed. Please check your credentials.');
      }

      // Get user details
      const details = await getOSMUserDetails();
      setUserDetails(details);
      setAuthSuccess(true);
      
      setTimeout(() => {
        onAuthenticated();
        onClose();
      }, 2000);

    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Authentication failed');
      clearOSMCredentials();
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleClose = () => {
    if (!isAuthenticating) {
      setCredentials({ username: '', password: '' });
      setAuthError(null);
      setAuthSuccess(false);
      setUserDetails(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-md">
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                <Key className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">OpenStreetMap Authentication</h2>
                <p className="text-gray-400 text-sm">Connect to add businesses to OSM</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isAuthenticating}
              className="p-2 text-gray-400 hover:text-white transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {authSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Authentication Successful!</h3>
              <p className="text-gray-400 mb-4">You can now add businesses directly to OpenStreetMap.</p>
              <div className="text-sm text-gray-500">
                Redirecting...
              </div>
            </div>
          ) : (
            <>
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-300 font-medium mb-1">Secure OSM Integration</p>
                    <p className="text-blue-200/80 text-xs leading-relaxed">
                      Your credentials are used only to authenticate with OpenStreetMap's official API. 
                      We don't store your password - it's only used for this session.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleAuthenticate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    OSM Username
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      required
                      value={credentials.username}
                      onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your OSM username"
                      disabled={isAuthenticating}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    OSM Password
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      required
                      value={credentials.password}
                      onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your OSM password"
                      disabled={isAuthenticating}
                    />
                  </div>
                </div>

                {authError && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <span className="text-red-300 text-sm">{authError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isAuthenticating || !credentials.username || !credentials.password}
                  className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {isAuthenticating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4" />
                      Connect to OpenStreetMap
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-4 border-t border-gray-700">
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-2">Don't have an OSM account?</p>
                  <a
                    href="https://www.openstreetmap.org/user/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-green-400 hover:text-green-300 text-sm transition-colors"
                  >
                    Create Account on OpenStreetMap
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};